/**
 * Deutsche Beschriftung der Quarantäne-Gründe.
 *
 * Jeder Code steht für „angenommen, nicht angewendet" — die Revision liegt vollständig in der
 * Datenbank, es entstand aber bewusst keine Buchung. Die Texte sagen deshalb beides: was fehlt und
 * was der Betreiber damit tun kann. Ein unbekannter Code fällt auf den Rohwert zurück statt zu
 * verschwinden; die Liste hier ist gegen die C#-Konstanten geschrieben, nicht aus dem Gedächtnis.
 */
export const QUARANTINE_REASON: Record<string, string> = {
  // Room-Identität — der häufigste Fall.
  room_identity_bookingcom_missing:
    'Diese Booking.com-Buchung hat mehrere Zimmer, aber keines davon trägt eine Kennung — weder ota_unique_id noch eine Zimmer-Reservierungsnummer. Bei mehreren Zimmern liesse sich ein späteres Storno sonst nicht dem richtigen Apartment zuordnen.',
  room_identity_airbnb_multi_room:
    'Diese Airbnb-Buchung umfasst mehr als ein Zimmer. Airbnb liefert keine Zimmerkennung, und eine Reservierung gilt normalerweise genau einem Listing — welches Zimmer gemeint ist, ist hier offen.',
  room_identity_unresolvable:
    'Der Buchung fehlt die eigene Kennung, aus der die Zimmer-Identität abgeleitet würde.',
  // Wird nicht mehr vergeben; bleibt für Revisionen aus der Zeit davor lesbar.
  room_identity_airbnb_missing:
    'Airbnb hat für mindestens ein Zimmer keine booking_room_id geliefert.',
  room_identity_channel_unsupported:
    'Der Kanal dieser Buchung ist weder Booking.com noch Airbnb. Für andere Kanäle ist keine Zimmer-Identität festgelegt.',
  room_identity_duplicate: 'Zwei Zimmer tragen dieselbe Kennung — welche gemeint ist, ist offen.',
  room_raw_identity_duplicate:
    'Zwei Zimmer teilen sich eine der Rohkennungen, auch wenn die verwendete eindeutig ist.',
  new_room_identity_duplicate: 'Zwei Zimmer der neuen Buchung tragen dieselbe Kennung.',
  new_room_raw_identity_duplicate: 'Zwei Zimmer der neuen Buchung teilen sich eine Rohkennung.',
  room_cancel_identity_unknown: 'Das stornierte Zimmer ist unter keiner bekannten Kennung geführt.',

  // Zuordnung beim Kanalmanager — der erste Stolperstein nach jedem Kanal-Anschluss.
  new_room_not_mapped:
    'Channex konnte Zimmer oder Rate dieser Buchung keinem Zimmertyp zuordnen — im Kanal fehlt das Mapping. Zu beheben in Channex unter Channels → Kanal → Mapping; danach kommen die Buchungen nach.',

  // Zuordnung zu unserem Bestand.
  new_unit_route_missing:
    'Zu einem Zimmertyp dieser Buchung gibt es keine veröffentlichte Apartment-Bindung. Meist ist das Apartment noch nicht veröffentlicht oder wurde neu gebunden.',
  new_unit_rate_plan_mismatch:
    'Der Ratenplan des Zimmers ist nicht der Standard-Ratenplan des gebundenen Apartments.',
  new_parent_route_stale:
    'Die Objekt-Bindung hat sich geändert, während die Revision verarbeitet wurde.',
  new_property_duplicate: 'Zwei Zimmer zeigen auf dasselbe Apartment.',
  room_unit_binding_missing: 'Die Apartment-Bindung des Zimmers existiert nicht mehr.',
  room_unit_reassignment_unsupported:
    'Ein Zimmer wurde bei der OTA auf ein anderes Apartment umgehängt — das wird nicht automatisch nachgezogen.',
  room_reactivation_unsupported: 'Ein bereits storniertes Zimmer wurde bei der OTA wiederbelebt.',

  // Inhalt der Revision.
  new_payload_invalid: 'Die Revision ist unvollständig oder überschreitet erlaubte Feldlängen.',
  mutation_payload_invalid: 'Die Änderungs-Revision ist unvollständig oder zu lang.',
  new_currency_invalid: 'Die Währung der Buchung passt nicht zu der des Objekts.',
  new_room_amount_missing: 'Einem Zimmer fehlt der Betrag.',
  new_room_amount_invalid: 'Der Betrag eines Zimmers ist nicht verwertbar.',
  new_room_date_missing: 'Einem Zimmer fehlen An- oder Abreise.',
  new_room_date_invalid: 'An- und Abreise eines Zimmers ergeben keinen Zeitraum.',
  new_room_occupancy_missing: 'Einem Zimmer fehlt die Belegung.',
  new_room_occupancy_invalid: 'Die Belegung eines Zimmers ist nicht verwertbar.',
  new_cancelled_room_unsupported:
    'Die neue Buchung enthält bereits ein storniertes Zimmer — dieser Fall wird nicht automatisch verarbeitet.',

  // Zustand und Reihenfolge.
  new_context_invalid: 'Der Bearbeitungskontext der neuen Buchung war nicht mehr gültig.',
  mutation_context_invalid: 'Der Bearbeitungskontext der Änderung war nicht mehr gültig.',
  mutation_current_graph_invalid:
    'Der gespeicherte Stand der Buchung passt nicht zur eingehenden Änderung.',
  mutation_status_unsupported: 'Der Status dieser Änderungs-Revision wird nicht verarbeitet.',
  revision_status_unsupported: 'Der Status dieser Revision wird nicht verarbeitet.',
  revision_modified_unsupported:
    'Eine Änderung traf auf eine Buchung, die es hier nicht gibt — meist, weil schon die erste Revision quarantänisiert wurde.',
  revision_cancelled_unsupported:
    'Ein Storno traf auf eine Buchung, die es hier nicht gibt — meist, weil schon die erste Revision quarantänisiert wurde.',
  revision_order_ambiguous: 'Die Reihenfolge zweier Revisionen ist nicht eindeutig bestimmbar.',
  revision_order_unverifiable: 'Die Reihenfolge der Revisionen liess sich nicht belegen.',
  mutation_reservation_missing:
    'Die Änderung verweist auf eine Reservierung, die es hier nicht gibt.',
  mutation_reservation_graph_invalid:
    'Der gespeicherte Reservierungs-Verbund passt nicht zur Änderung.',
  mutation_parent_identity_mismatch:
    'Die Änderung nennt eine andere Reservierungs-Identität als die gespeicherte.',
  mutation_revision_pointer_mismatch:
    'Der Revisions-Zeiger der Änderung passt nicht zum gespeicherten Stand.',
  mutation_currency_changed:
    'Die Währung der Buchung hat sich geändert — das wird nicht automatisch nachgezogen.',
  mutation_availability_range_invalid:
    'Der von der Änderung betroffene Zeitraum ergibt keine gültige Spanne für die Verfügbarkeit.',
  mutation_availability_current_child_missing:
    'Zu einem geänderten Zimmer fehlt die gespeicherte Buchung, aus der die Verfügbarkeit neu abgeleitet würde.',
  mutation_availability_validated_room_missing:
    'Ein geprüftes Zimmer fehlt bei der Neuableitung der Verfügbarkeit.',

  // Warten und erneutes Verarbeiten.
  revision_waits_for_earlier:
    'Eine frühere Revision derselben Buchung ist zuerst an der Reihe; diese folgt danach.',
  route_wait_failed:
    'Die Verarbeitung ist beim letzten Versuch abgebrochen; es wird automatisch erneut versucht.',
  replay_failed: 'Die erneute Verarbeitung ist abgebrochen; es entstand keine Buchung.',
  replay_finance_journal_missing:
    'Zu dieser Revision fehlt das Finanzjournal — ohne es entstünde eine Buchung ohne Preis und Rechnung. Der Support kann es nachladen.',
  replay_route_mismatch: 'Die Revision gehört inzwischen zu keinem Objekt dieses Workspace mehr.',
  replay_snapshot_invalid: 'Der gespeicherte Stand der Revision ist unvollständig oder beschädigt.',
  replay_schema_unsupported:
    'Der gespeicherte Stand stammt aus einer Version, die nicht mehr gelesen werden kann.',
  replay_status_unsupported: 'Der gespeicherte Stand hat eine Art, die nicht verarbeitet wird.',
  replay_status_mismatch: 'Der gespeicherte Stand passt nicht zur Art der Revision.',
  replay_identity_mismatch: 'Der gespeicherte Stand gehört zu einer anderen Revision.',
};

export function quarantineReason(code: string | null): string {
  if (!code) return 'Grund nicht festgehalten.';
  return QUARANTINE_REASON[code] ?? code;
}

/**
 * Der Grund im Zustand der Zeile: Was beim Warten „es wird erneut versucht" heißt, ist in der
 * Quarantäne ein Fall für den Support — mit der Kennung, unter der er die Revision findet.
 */
export function holdReason(
  code: string | null,
  row: { id: string; processingState: string },
): string {
  if (code === 'route_wait_failed' && row.processingState === 'Quarantined') {
    return `Die Verarbeitung ist bei jedem Versuch abgebrochen; es entstand keine Buchung. Bitte dem Support melden, Kennung ${row.id}.`;
  }
  return quarantineReason(code);
}

/**
 * Die drei Revisionsarten des Kanalmanagers (`ChannexRevisionStatus`) auf Deutsch. Ein
 * unbekannter Wert bleibt sichtbar, statt zu verschwinden — auf einer Forensikfläche ist das
 * Rohdatum die letzte Auskunft.
 */
const REVISION_STATUS_LABEL: Record<string, string> = {
  new: 'Neue Buchung',
  modified: 'Änderung',
  cancelled: 'Storno',
};

export function revisionStatusLabel(status: string): string {
  return REVISION_STATUS_LABEL[status.toLowerCase()] ?? status;
}
